import { VdTracking } from '@vend/vend.js'
import angular from 'angular'
import find from 'lodash/find'

import { StateDeclaration } from '@uirouter/angularjs'
import { STATES } from '../../../lib/routes'
import { $q, $rootScope, $transitions } from '../ng-compat/imports'
import {
  getUserAttributes,
  setUserAttribute,
} from '../components/users/users-resource/users-resource'
import {
  FEATURE_SSBO_ONBOARDING_COHORT,
  ONBOARDING_PRODUCT_SKU,
  RETAILER_SETTINGS,
  TOUR_STEPS,
  TOUR_TYPE,
  USER_ATTRIBUTES,
} from '../constants'
import { getFeatureFromCache, initFeatureCache } from '../util/feature-cache'
import { EventEmitter } from '../util/event-emitter'
import { SaleManager } from './sale-manager'
import { UserManager } from './user-manager'
import { PosStateManager } from './pos-state-manager'
import { RetailerSettingsManager } from './retailer-settings-manager'

type Events = {
  tourEnabledChange: [$TSFixMe]
  trainingModeEnabledChange: [$TSFixMe]
}

/**
 * Responsible for managing training mode.
 *
 * @class TrainingModeManager
 * @constructor
 * @extends {EventEmitter}
 */
export class TrainingModeManager extends EventEmitter<Events> {
  private _onBoardingCustomised?: boolean
  private _tourType?: TOUR_TYPE
  private _unbindPreventStateChange: $TSFixMe
  private _unbindStateChangeSuccessListener: $TSFixMe
  public trainingModeOnboardingState?: boolean

  constructor(
    private retailerSettingsManager: RetailerSettingsManager,
    private posStateManager: PosStateManager,
    private userManager: UserManager,
    private routeManager: $TSFixMe,
    private vdTracking: VdTracking,
    private saleManager: SaleManager,
    private productSkuIndex: $TSFixMe,
    wrDebugTools: $TSFixMe
  ) {
    'ngInject'
    super()
    wrDebugTools.register('trainingModeManager')
  }

  /**
   * Initialises Training Mode Manager
   *
   * @method init
   */
  public init() {
    // Check persisted pos state for whether training mode is enabled
    if (!this.posStateManager.getTrainingModeEnabled()) {
      // Sort out welcome (either training splash or on-boarding tour)
      this._initiateWelcome()
    } else {
      // When something like a page refresh occurs, training mode is already enabled
      // Go with the persisted last used tour type
      this._tourType = this.posStateManager.getTourType()
      // Ensure the tour stays on & training mode is in the correct state again
      this.emit('tourEnabledChange', { tourType: this._tourType })
      if (this.posStateManager.getTrainingModeEnabled()) {
        this._setupTrainingMode()
      }
    }
    this._shouldPreventStateChange()
    this.posStateManager.on(
      'trainingModeEnabledChange',
      this._onTrainingModeEnabledChange,
      this
    )
    this.posStateManager.on(
      'tourEnabledChange',
      this._onTourEnabledChange,
      this
    )
    // Allows a welcome tour to be initiated when user switching
    this.userManager.on('loggedInUserChange', this._initiateWelcome, this)
  }

  /**
   * Whether training mode is currently enabled or not.
   *
   * @property trainingModeEnabled
   * @readOnly
   */
  public get trainingModeEnabled() {
    return this.posStateManager.getTrainingModeEnabled()
  }

  /**
   * Whether a tour is currently enabled or not.
   *
   * @property tourEnabled
   * @readOnly
   */
  public get tourEnabled() {
    return this.posStateManager.getTourEnabled()!
  }

  /**
   * Set the register to enter or leave a tour.
   *
   * @method setTourEnabled
   *
   * @param  enableTour {Boolean}
   *         true if the register should trigger a tour, false otherwise
   */
  public set tourEnabled(enableTour: boolean) {
    this.posStateManager.setTourEnabled(enableTour)
    if (enableTour) {
      this.initiateTour()
    } else {
      this.exitTour()
    }
  }

  /**
   * The tour steps (depending on tour type). Note this is the main part where tour type comes into play.
   *
   * @method getTourSteps
   *
   * @return {Promise<Array>} Resolved once the correct tour steps have been determined and customised.
   */
  public getTourSteps() {
    return $q((resolve, reject) => {
      if (this._tourType === TOUR_TYPE.ONBOARDING) {
        resolve(TOUR_STEPS.ONBOARDING)
      } else if (this._tourType === TOUR_TYPE.PRIMARY_ONBOARDING) {
        if (!this._onBoardingCustomised) {
          this._customiseSearchBarStep().then(
            () => resolve(TOUR_STEPS.PRIMARY_ONBOARDING),
            reject
          )
        } else {
          resolve(TOUR_STEPS.PRIMARY_ONBOARDING)
        }
      } else if (this._tourType === TOUR_TYPE.TRAINING) {
        const tourSteps = TOUR_STEPS.TRAINING
        resolve(tourSteps)
      }
    })
  }

  /**
   * The currently active tour type
   *
   * @property currentTourType
   * @readOnly
   */
  public get currentTourType() {
    return this._tourType
  }

  /**
   * Responsible for initiating training mode.
   *
   * @method initiateTrainingMode
   *
   * @return {Boolean} True if training mode is enabled, false otherwise
   */
  public initiateTrainingMode() {
    if (!this.trainingModeEnabled) {
      this._setupTrainingMode()
      this.posStateManager.setTrainingModeEnabled(true)
      return true
    } else {
      return false
    }
  }

  /**
   * Responsible for initiating tour if permitted.
   *
   * @method initiateTour
   * @return {Boolean} True if tour is enabled, false otherwise
   */
  public initiateTour() {
    if (this.trainingModeEnabled) {
      this.posStateManager.setTourEnabled(true)
      return this.tourEnabled
    } else {
      return false
    }
  }

  /**
   * Forces the tour type to training tour, re-sets up training mode & initiates tour.
   *
   * @method initiateTrainingTour
   * @return {Boolean} True if tour is enabled, false otherwise
   */
  public initiateTrainingTour() {
    // Ensure any in-progress tour is exited (e.g. can happen with the app switcher tour)
    this.exitTour()
    // Force tour type
    this._tourType = TOUR_TYPE.TRAINING
    this.posStateManager.setTourType(this._tourType)
    this._setupTrainingMode()
    return this.initiateTour()
  }

  /**
   * Responsible for completing the training mode and tour (if enabled)
   *
   * @method exitTrainingMode
   *
   * @return {Promise} A promise resolved when the saleManager clears the training mode sale
   */
  public exitTrainingMode() {
    return this.saleManager.clearTrainingModeSale().then(() => {
      this._cleanupTrainingMode()
      this._setUserAttributeNewToRegister(false)
      this.posStateManager.setTrainingModeEnabled(false)
      this.exitTour()
    })
  }

  /**
   * Responsible for disabling the current tour. Note that it does not mark tour as complete via the user attribute as
   * this is done at explicit points in tours.
   *
   * @method exitTour
   */
  public exitTour() {
    if (this.tourEnabled) {
      this.posStateManager.setTourEnabled(false)
    }
  }

  /**
   * Responsible for disabling the training mode. It doesn't mark it as completed
   *
   * @method disableTrainingMode
   */
  public disableTrainingMode() {
    this.posStateManager.setTrainingModeEnabled(false)
  }

  // ============================== Private ============================== //
  /**
   * Responsible for setting the title of the search bar step. Currently only used within the primary on boarding
   * tour. This is so we can set the name of the customised product that is added when a particular retailer type is
   * chosen during signup.
   *
   * @private
   * @method _customiseSearchBarStep
   *
   * @return {Promise} Resolved once the onboarding product has been found and the tour step title has been set.
   */
  private _customiseSearchBarStep() {
    const searchBarStep = find(TOUR_STEPS.PRIMARY_ONBOARDING, {
      id: 'searchBar',
    })!
    return this.productSkuIndex
      .search(ONBOARDING_PRODUCT_SKU)
      .then((results: $TSFixMe) => {
        this._onBoardingCustomised = true
        if (results.length) {
          searchBarStep.title = `Search for "${results[0].product.name}"`
        }
      })
  }

  /**
   * Get tour type in relation to current retailer settings
   *
   * @private
   * @method _getTourType
   *
   * @return {String} the type of tour that should be triggered
   */
  private _getTourType() {
    const user = this.userManager.getLoggedInUser()
    const isPrimaryUser = Boolean(
      user && (user as $TSFixMe)[USER_ATTRIBUTES.USER_IS_PRIMARY]
    )
    const retailerSettings = this.retailerSettingsManager.get()
    const registerIsDefault =
      retailerSettings[RETAILER_SETTINGS.REGISTER_IS_DEFAULT]
    let tourType = TOUR_TYPE.TRAINING
    if (registerIsDefault) {
      tourType = TOUR_TYPE.ONBOARDING
      if (isPrimaryUser) {
        tourType = TOUR_TYPE.PRIMARY_ONBOARDING
      }
    }
    return tourType
  }

  /**
   * Returns true if the current tour type is an on-boarding type.
   *
   * @private
   * @method _isTourTypeOnboarding
   * @return {Boolean} true if current tour type is on-boarding (either standard or primary user), otherwise false
   */
  private _isTourTypeOnboarding() {
    return (
      this._tourType === TOUR_TYPE.ONBOARDING ||
      this._tourType === TOUR_TYPE.PRIMARY_ONBOARDING
    )
  }

  /**
   * Returns true if the current tour type is training
   *
   * @private
   * @method _isTourTypeTraining
   * @return {Boolean} true if current tour type is training, otherwise false
   */
  private _isTourTypeTraining() {
    return this._tourType === TOUR_TYPE.TRAINING
  }

  /**
   * Set the training mode state events & variables.
   *
   * @private
   * @method _setupTrainingMode
   */
  private _setupTrainingMode() {
    // Training mode can be either on-boarding or training state
    this._setTrainingModeOnboardingState()
    this._unbindStateChangeSuccessListener = $transitions.onSuccess(
      {},
      transition => {
        if (
          transition.from().name === STATES.PAYMENT &&
          transition.to().name === STATES.SALE
        ) {
          // Throw an event when state changes from payment screen back to current sale (when in training mode only)
          // This is used to trigger the correct step in the tour if the payment screen is dismissed
          $rootScope.$emit('training-sale:dismiss-payment-screen')
        }
        if (
          transition.from().name === STATES.SALE &&
          transition.to().name === STATES.SALE_CONFIRMATION
        ) {
          // Throw an event when state changes from the current sale screen immediately to sale confirmation (when in
          // training mode only). This is used to trigger the correct step in the tour if the sale confirmation screen
          // is forced on refresh (or similar).
          $rootScope.$emit('training-sale:force-sale-confirmation-screen')
        }
      }
    )
  }

  /**
   * Clean up the training mode state events & variables.
   *
   * @private
   * @method _cleanupTrainingMode
   */
  private _cleanupTrainingMode() {
    this._clearTrainingModeOnboardingState()
    this._unbindStateChangeSuccessListener()
  }

  /**
   * Set the training mode on-boarding state depending on tour type to show different topbar content, confirmation screen content etc.
   *
   * @private
   * @method _setTrainingModeOnboardingState
   */
  private _setTrainingModeOnboardingState() {
    this.trainingModeOnboardingState = false
    if (this._isTourTypeOnboarding()) {
      this.trainingModeOnboardingState = true
    }
    this.posStateManager.setTrainingModeOnboardingState(
      this.trainingModeOnboardingState
    )
    return this.trainingModeOnboardingState
  }

  /**
   * Set back to training mode defaults ensure that onboarding states are cleared.
   *
   * @method _clearTrainingModeOnboardingState
   */
  private _clearTrainingModeOnboardingState() {
    this.trainingModeOnboardingState = false
    this.posStateManager.setTourType(TOUR_TYPE.TRAINING)
  }

  /**
   * Handle training mode being toggled. Fires tracking and change events as appropriate and determines if we should
   * prevent state changes based on whether training mode is enabled.
   *
   * @private
   * @method _onTrainingModeEnabledChange
   *
   * @param  event {Event}
   *         Details of the change event
   */
  private _onTrainingModeEnabledChange(event: $TSFixMe) {
    const isEnabled = event.newValue
    if (isEnabled) {
      this.vdTracking.trackUserEvent({
        eventName: 'Training Mode Enabled',
      })
    }
    this._shouldPreventStateChange()
    this.emit('trainingModeEnabledChange', event)
  }

  /**
   * Manages tour being toggled.
   *
   * @private
   * @method  _onTourEnabledChange
   *
   * @param  event {Event}
   *         Details of the change event
   */
  private _onTourEnabledChange(event: $TSFixMe) {
    const isEnabled = event.newValue
    if (this._isTourTypeTraining() && isEnabled) {
      this.vdTracking.trackUserEvent({
        eventName: 'Tour Initiated',
      })
    } else {
      if (isEnabled) {
        this.vdTracking.trackUserEvent({
          eventName: this._tourType!,
          metadata: {
            step: 'Tour Initiated',
          },
        })
      }
    }
    this.posStateManager.setTourType(this._tourType!)
    this.emit('tourEnabledChange', event)
  }

  /**
   * Determines if we should prevent state changes when there is a change to trainingModeEnabledChange property on
   * the posStateManger
   *
   * @private
   * @method _shouldPreventStateChange
   */
  private _shouldPreventStateChange() {
    if (this.trainingModeEnabled) {
      this._unbindPreventStateChange = $transitions.onStart({}, transition => {
        this._preventNonSaleStateChange(transition, transition.to())
      })
    } else {
      // unbind
      if (this._unbindPreventStateChange) {
        this._unbindPreventStateChange()
        this._unbindPreventStateChange = null
      }
    }
  }

  /**
   * Ensures that the user can't change route and inadvertently enter a view where training mode
   * isn't currently applicable. Most probably caused by the user hitting the back button
   *
   * @param  {TransitionEvent} event
   *         The state event
   *
   * @param  {State} toState
   *         The state object
   *
   */
  private _preventNonSaleStateChange(
    transition: $TSFixMe,
    toState: StateDeclaration
  ) {
    if (!this.routeManager.isStateAllowedInTrainingMode(toState)) {
      transition.abort()
    }
  }

  /**
   * Used to trigger a Welcome path if user is new to Register.
   *
   * Brand new to Vend user --> Training mode + on-boarding tour forced
   *
   * @private
   * @method _initiateWelcome
   */
  private _initiateWelcome() {
    // Ensure any in-progress tour is exited (e.g. can happen when user switching)
    this.exitTour()
    // Await both retailer settings and feature flag in parallel before checking them.
    // initFeatureCache() is idempotent, it returns the same in-flight promise, so no duplicate network request is made.
    $q.all([initFeatureCache(), this.retailerSettingsManager.init()]).then(
      ([, retailerSettings]) => {
        // If onboarding tour is disabled via retailer settings or user is in the ssbo dashboard onboarding cohort skip the welcome tour and mark as completed
        if (
          retailerSettings.disable_onboarding_tour ||
          getFeatureFromCache(FEATURE_SSBO_ONBOARDING_COHORT)
        ) {
          // Clear new-to-register state which would otherwise be cleared when the splash screen is triggered
          // or the training tour completed
          this._setUserAttributeNewToRegister(false)
          return
        }
        getUserAttributes(this.userManager.getLoggedInUser().id)
          .then(data => {
            // Decide whether to show a welcome, based on "new to register" user attribute
            const newToRegister =
              data &&
              find(data, {
                name: USER_ATTRIBUTES.USER_IS_NEW_TO_REGISTER,
              })
            if (!newToRegister || newToRegister.value === 'true') {
              // Yes, welcome initiating
              const tourType = this._getTourType()
              // Set tour type & decide which type of welcome to show based on set tour type
              this._tourType = tourType
              this.posStateManager.setTourType(tourType)
              if (
                this._isTourTypeOnboarding() &&
                !this.saleManager.hasActiveSale()
              ) {
                // Initiate first-time-to-everything welcome tour
                this._initiateOnboarding()
              }
            }
          })
          // Error: assume no training mode necessary.
          .catch(angular.noop)
      }
    )
  }

  /**
   * Sets USER_IS_NEW_TO_REGISTER user attribute to the provided value. Used to mark tour as complete when false passed.
   *
   * @private
   * @method _setUserAttributeNewToRegister
   *
   * @param {Boolean} userNewToRegister
   *        The value to assign to USER_IS_NEW_TO_REGISTER user attribute
   */
  private _setUserAttributeNewToRegister(userNewToRegister: boolean) {
    setUserAttribute({
      user_id: this.userManager.getLoggedInUser().id,
      name: USER_ATTRIBUTES.USER_IS_NEW_TO_REGISTER,
      value: userNewToRegister.toString(),
    })
      // Not much we can do about failure. Suppress the error.
      .catch(angular.noop)
  }

  /**
   * Trigger the welcome to on-boarding tour.
   *
   * This is shown when users are fresh to Vend as well as first-time-to-register (i.e. have never experienced the
   * old sell screen)
   *
   * @method _initiateOnboarding
   */
  private _initiateOnboarding() {
    if (this.initiateTrainingMode()) {
      this.initiateTour()
    }
  }
}

/**
 * Responsible for managing training mode.
 *
 * @module webregister.managers.trainingModeManager
 */
angular
  .module('webregister.managers')
  .service('trainingModeManager', TrainingModeManager)
